import React, { useState, useEffect, forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useOnClickOutside from 'use-onclickoutside';
import Paper from '@material-ui/core/Paper';

import Button from './KeypadButton';
import Display from './Display';
import { media } from '../styles/media-templates';
import useKeyboardInput from '../hooks/useKeyboardInput';
import IconBackspace from '@material-ui/icons/BackspaceSharp'
import Grid from '@material-ui/core/Grid'
import { makeStyles } from '@material-ui/core'
import { numpadStyles } from '../styles'

const Content = styled(Paper)`
  display: flex;
  flex-direction: column;
  ${media.mobile`width: 100%;`} 
  background: transparent !important;
  
  ${props =>
    props.position === 'fullscreen'
      ? `
  width: 100vw;
  height: 100vh;
  font-size: 1.2em;
  `
      : `
      width: 100%;
  `}
  
  background: ${props => props.theme.body.backgroundColor};
  transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms;
  box-shadow: none !important;
`;


const Keys = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-grow: 1;
  // max-width: 400px;
  // ${media.mobile`max-width: 360px !important;`}
  background: ${props => props.theme.header.secondaryColor};
  button {
    border-bottom: 1px solid #ddd;
    border-right: 1px solid #ddd;
  }
  button:nth-child(3n) {
    border-right: none;
  }
  button:nth-child(-n + 3) {
    border-top: 1px solid #ddd;
  }
`

const useStyles = makeStyles(() => numpadStyles)

const KeyPad = forwardRef(function KeyPad(
  { displayRule, position, validation, label, confirm, cancel, keyValid, value, update, sync, show, enter, loading, placeholder },
  ref
) {
  // @ts-ignore
  const keypadKeys = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '', '0', 'backspace']
  const keyboardKeys = [...Array(10).keys()].map(v => v.toString())
  const [inputValue, setInputValue] = useState(value)
  const keyboard = useKeyboardInput(
    inputValue,
    keyboardKeys.filter(key => keyValid(inputValue, key))
  )
  const classes = useStyles()

  function computeNextKey(newValue, key) {
    let computedValue;
    if (keyValid(inputValue, key)) {
      if (key === '-') {
        computedValue = inputValue.charAt(0) === '-' ? inputValue.substr(1) : `-${inputValue}`;
      } else if (key === '.') {
        const leadingZero = ['', '-'].includes(inputValue);
        computedValue = `${inputValue}${leadingZero ? '0' : ''}${key}`;
      } else {
        computedValue = newValue;
      }
      setInputValue(computedValue);
      if (sync) {
        update(computedValue);
      }
    }
  }

  useOnClickOutside(ref, e => {
    // if (validation(inputValue)) {
    //   confirm(inputValue);
    // } else {
    //   cancel();
    // }
  });

  // Reload props.value into the state
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (keyboard.keyDownEvent) {
      /** useKeyBaordInput set null when initializing the initialValue to avoid this computation before validation  */
      if (['Enter', 'Tab'].includes(keyboard.keyDownEvent.key) && validation(keyboard.value)) {
        confirm(keyboard.value);
      } else if (['Escape'].includes(keyboard.keyDownEvent.key)) {
        cancel();
      } else if (['Backspace'].includes(keyboard.keyDownEvent.key)) {
        if (keyboard.keyDownEvent.ctrlKey || keyboard.keyDownEvent.altKey) {
          setInputValue('');
        } else {
          setInputValue(keyboard.value);
        }
      } else {
        computeNextKey(keyboard.value, keyboard.keyDownEvent.key);
      }
    }
  }, [keyboard.value, keyboard.keyDownEvent]);

  const onButtonClick = useCallback(
    clickedKey => {
      if (!clickedKey) return;
      if (clickedKey === 'backspace') {
        if (inputValue.length > 0) {
          const newValue = inputValue.slice(0, -1);
          setInputValue(newValue);
          if (sync) {
            update(newValue);
          }
        }
      } else {
        keyboard.virtualInteraction(clickedKey.toString());
      }
    },
    [inputValue, sync, update]
  );

  return (
    <Content position={position} ref={ref} square>
      <Grid container spacing={3} justify="center" alignItems="center">
        <Display
          value={displayRule(inputValue)}
          backspace={() => keyboard.virtualInteraction('Backspace')}
          longPressBackspace={() => setInputValue('')}
          show={show}
          enter={enter}
          loading={loading}
          placeholder={placeholder}
        />
      </Grid>

      <Grid item xs={12} className="numpad-keys-caption">
        <span>e.g. 7430</span>
      </Grid>

      { (show && !loading) &&
      <Grid container spacing={3}>
      <Grid item xs={12} className="numpad-keys">
        <Grid  container spacing={3} justify="center" alignItems="center" className={classes.keys}>
          {keypadKeys.map(key => (
            <Grid key={`button-gid-${key}`} item xs={4} className={classes.keyWrapper}>
              <Button
                key={`button-${key}`}
                click={onButtonClick}
                value={key}
                disabled={key === '' ? false : (key === 'backspace' ? inputValue.length === 0 : !keyValid(inputValue, key))}
                className={classes.keyButton}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
      </Grid>
      }
    </Content>
  )
})

KeyPad.displayName = 'KeyPad'; // TODO: necessary?

KeyPad.propTypes = {
  label: PropTypes.string,
  confirm: PropTypes.func.isRequired,
  cancel: PropTypes.func,
  displayRule: PropTypes.func.isRequired,
  validation: PropTypes.func.isRequired,
  keyValid: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sync: PropTypes.bool,
  show: PropTypes.bool,
  loading: PropTypes.bool,
  update: PropTypes.func.isRequired,
  position: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
};

KeyPad.defaultProps = {
  label: undefined,
  value: '',
  sync: false,
  cancel: () => {},
  show: true,
  loading: false,
  placeholder: '',
};

export default KeyPad;
